//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import Icon from '@components/atoms/icon/Icon';
import { BodyText, CaptionErrorText } from '@components/atoms/text/Text';
import { FormFieldController } from '@components/form/field-controller/FormFieldController';
import { RadioGroup } from '@components/primitives/radio-group/RadioGroup';

type AllowedKeys = 'whoShipsProduct' | 'codeSupplier';

export interface RadioItem {
  label: string;
  trackingId: string;
  value: string;
}

interface CampaignRequestRadioFieldProps {
  isDisabled?: boolean;
  label: string;
  labelHelpText: string;
  name: AllowedKeys;
  options: RadioItem[];
  onChange?: (value: string) => void;
}

export const CampaignRequestRadioField = ({
  isDisabled,
  label,
  labelHelpText,
  name,
  onChange,
  options,
}: CampaignRequestRadioFieldProps) => {
  const { control } = useFormContext<SamplingCampaignRequest>();

  return (
    <Container>
      <Title>
        <BodyText>{label}</BodyText>
        <HelpIcon helpText={labelHelpText} />
      </Title>
      <FormFieldController
        control={control}
        name={`fulfillmentOptions.${name}`}
        rules={{ required: `Please select a ${label.toLowerCase()}.` }}
      >
        {({ disabled, error, onChange: fieldOnChange, value, ...props }) => (
          <>
            <StyledRadioGroup
              {...props}
              disabled={isDisabled ?? disabled}
              onValueChange={(newValue) => {
                fieldOnChange(newValue);
                if (onChange) {
                  onChange(newValue);
                }
              }}
              value={value}
            >
              {options.map((item) => (
                <RadioGroupItemWrapper key={item.value}>
                  <StyledRadioGroupItem
                    id={item.value}
                    trackingId={item.trackingId}
                    value={item.value}
                  >
                    <RadioGroup.Indicator />
                  </StyledRadioGroupItem>
                  <StyledRadioGroupLabel htmlFor={item.value}>
                    <BodyText>{item.label}</BodyText>
                  </StyledRadioGroupLabel>
                </RadioGroupItemWrapper>
              ))}
            </StyledRadioGroup>
            {error && (
              <ErrorText>
                <StyledIcon name='error' />
                <CaptionErrorText>{error.message}</CaptionErrorText>
              </ErrorText>
            )}
          </>
        )}
      </FormFieldController>
    </Container>
  );
};

const Container = styled.div`
  margin-top: ${({ theme }) => theme.spacings[4]};
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const StyledRadioGroup = styled(RadioGroup)`
  margin-top: ${({ theme }) => theme.spacings[1]};
  display: flex;
  gap: ${({ theme }) => theme.spacings[2]};
`;

const RadioGroupItemWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRadioGroupItem = styled(RadioGroup.Item)`
  margin-right: ${({ theme }) => theme.spacings[1]};
`;

const StyledRadioGroupLabel = styled(RadioGroup.Label)`
  display: flex;
  align-items: center;
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIcon = styled(Icon)(
  ({ theme }) => `
    margin-right: ${theme.spacings[1]};
    color: ${theme.colors.aliases.error};
    height: calc(${theme.spacings[1]} * 3);
    width: calc(${theme.spacings[1]} * 3);
`,
);
